import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Reset window scroll on route changes.
 *
 * React Router intentionally preserves scroll position across navigations
 * (it's a SPA, not a series of full page loads). For a content-heavy
 * member portal that's confusing — clicking "Calendar" from halfway down
 * the Dashboard leaves the user halfway down the Calendar. This component
 * mounts inside <BrowserRouter> and snaps scroll to the top whenever the
 * pathname changes.
 *
 * Hash links (e.g. /profile#pay-dues) bypass the snap and scroll to the
 * anchor element instead, so deep-links continue to work.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      // Defer one frame so the destination route has time to mount.
      const id = window.requestAnimationFrame(() => {
        const target = document.querySelector(hash)
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else {
          window.scrollTo({ top: 0, left: 0 })
        }
      })
      return () => window.cancelAnimationFrame(id)
    }

    window.scrollTo({ top: 0, left: 0 })
    return undefined
  }, [pathname, hash])

  return null
}

export default ScrollToTop
